import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@tripian/core";

const useVisaAuth = (isLoggedIn: boolean) => {
  const [visaToken, setVisaToken] = useState<string | undefined>(undefined);
  const [loadingVisaToken, setLoadingVisaToken] = useState(false);
  const [hasError, setHasError] = useState(false);
  // The hook's own effect and the page's retry effect can both fire in the same render; share one request
  const inFlightRef = useRef<Promise<string | undefined> | null>(null);

  const fetchToken = useCallback(async () => {
    if (
      !isLoggedIn ||
      window.tconfig?.ELIGIBILITY_TYPE !== "visa" ||
      hasError
    ) {
      return;
    }

    if (inFlightRef.current) return inFlightRef.current;

    const request = (async (): Promise<string | undefined> => {
      setLoadingVisaToken(true);

      try {
        const response = await api.visaIframeToken();

        if (response?.token) {
          setVisaToken(response.token);
          setHasError(false);
          return response.token;
        } else {
          console.error("Empty or invalid token response");
          setHasError(true);
        }
      } catch (err) {
        console.error("visaIframeToken error:", err);
        setHasError(true);
      } finally {
        inFlightRef.current = null;
        setLoadingVisaToken(false);
      }
    })();

    inFlightRef.current = request;
    return request;
  }, [isLoggedIn, hasError]);

  useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  const validateVisa = async (context: string) => {
    if (!context) {
      console.error("validateVisa: context is empty");
      return null;
    }

    try {
      const res = await api.visaValidatorCheck(context);

      if (!res) {
        console.error("validateVisa: response is empty");
        return null;
      }

      if (!res.userType) {
        console.error("validateVisa: userType is missing");
        return null;
      }

      return res;
    } catch (err) {
      console.error("visaValidatorCheck error:", err);
      return null;
    }
  };

  const resetError = useCallback(() => {
    setHasError(false);
  }, []);

  return {
    visaToken,
    loadingVisaToken,
    hasError,
    validateVisa,
    refreshVisaToken: fetchToken,
    resetError,
  };
};

export default useVisaAuth;
